import classnames from 'classnames'

import { STATSIG_EXPERIMENTS_CONFIG } from '@src/config/statsig-experiments.config'
import { STATSIG_EXPERIMENTS, useStatsigExperiment } from '@src/experiments/statsig-integration'
import { FeatureSectionTwo } from '@src/quince-components/src'
import { IconWithTextfeatureProps } from '@src/quince-components/src/atoms/iconWithTextfeature/IconWithTextfeature'
import CustomSection from '@src/quince-site-common/src/domains/CustomSection'
import CUSTOM_SECTION_TYPE from '@src/quince-site-common/src/domains/CustomSectionType'
import RichText from '@src/quince-site-common/src/domains/RichText'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { computeScrollPercentage } from '@src/quince-site-common/src/hooks/useScrollEventTracker'
import { useTrackIsElementVisible } from '@src/quince-site-common/src/hooks/useTrackIsElementVisible'
import BundlePageSectionProps from '@src/quince-site-pdp/src/domains/BundlePageSectionProps'
import ProductPageSectionProps from '@src/quince-site-pdp/src/domains/ProductPageSectionProps'

import { isFS2NewDesignDataValid, isFS2SectionDataValid } from '../../../helper/product.helper'
import { Section as RowOfImageTextBlocksSection } from '../rowOfImageTextBlocks/components/Section'

import styles from './imageWithTextProductDetailsSection.module.scss'

const FEATURE_SECTION_2_HEADING = 'Feature Section'

function ImageWithTextProductDetailsSection(props: ProductPageSectionProps | BundlePageSectionProps) {
  const product = 'bundle' in props ? props.bundle : props.product

  const { deviceType } = useDeviceInfo()
  const { experimentBucket: featureSection2DesignBucket } = useStatsigExperiment(
    STATSIG_EXPERIMENTS.FEATURE_SECTION_2_DESIGN,
  )

  const isDesktop = deviceType === DEVICE_TYPES.DESKTOP
  const desktopSection = product.desktopSections.find(ds => ds.sectionType === CUSTOM_SECTION_TYPE.FEATURE_SECTION_2)
  const mobileSection = product.mobileSections.find(ds => ds.sectionType === CUSTOM_SECTION_TYPE.FEATURE_SECTION_2)
  const sectionDetails = isDesktop ? desktopSection : mobileSection
  const containerClassName = isDesktop ? 'desktopOnly' : 'mobileOnly'

  const getIsVisibleTrackParams = () => {
    return {
      action: 'feature_section_two_viewed',
      value: true,
      context: {
        contentContainerType: sectionDetails?.contentContainerType,
        contentContainerName: sectionDetails?.contentContainerName,
        trackingId: sectionDetails?.trackingId,
        contentContainerDisplayName: sectionDetails?.displayTitle,
        is_in_first_fold: computeScrollPercentage() === 0,
      },
    }
  }
  const { ref: trackIsVisibleRef } = useTrackIsElementVisible(getIsVisibleTrackParams)

  if (!sectionDetails) return null

  const hideFeatureSection2 =
    featureSection2DesignBucket ===
    STATSIG_EXPERIMENTS_CONFIG[STATSIG_EXPERIMENTS.FEATURE_SECTION_2_DESIGN].buckets?.groupB
  if (hideFeatureSection2) return null

  // If FS2 new design data is not valid, fall back to old UI.
  const showNewDesign =
    featureSection2DesignBucket ===
      STATSIG_EXPERIMENTS_CONFIG[STATSIG_EXPERIMENTS.FEATURE_SECTION_2_DESIGN].buckets?.groupA &&
    isFS2NewDesignDataValid(sectionDetails)
  if (showNewDesign) {
    return (
      <div ref={trackIsVisibleRef}>
        <RowOfImageTextBlocksSection
          sectionDetails={sectionDetails}
          containerClassName={classnames(styles.fs2NewDesign, containerClassName)}
          isFS2
        />
      </div>
    )
  }

  if (!isFS2SectionDataValid(sectionDetails)) return null
  return (
    <div ref={trackIsVisibleRef} className={classnames(styles.imageWithTextProductDetailsSection, containerClassName)}>
      <Section sectionDetails={sectionDetails} />
    </div>
  )
}

interface SectionProps {
  sectionDetails: CustomSection
}

const Section = ({ sectionDetails }: SectionProps) => {
  const iconsWithTextDesktop: IconWithTextfeatureProps[] = (sectionDetails.subSections ?? []).map(pss => ({
    iconProps: {
      title: pss.images ? (pss.images[0]?.title ?? '') : '',
      url: pss.images ? `https:${pss.images[0]?.url}` : '',
    },
    heading: pss.displayTitle ?? '',
    description: RichText.makeRichTextProps(pss.richTexts ? pss.richTexts[0] : undefined),
  }))

  return (
    <FeatureSectionTwo
      heading={FEATURE_SECTION_2_HEADING}
      imageProps={{
        alt: sectionDetails.images?.length ? sectionDetails.images[0]?.title : '',
        title: sectionDetails.images?.length ? (sectionDetails.images[0]?.title ?? '') : '',
        url: sectionDetails.images?.length ? `https:${sectionDetails.images[0]?.url}` : '',
      }}
      videoProps={{
        id: sectionDetails.videos?.length ? `https:${sectionDetails.videos[0]?.id}` : '',
        url: sectionDetails.videos?.length ? `https:${sectionDetails.videos[0]?.url}` : '',
        contentType: sectionDetails.videos?.length ? `https:${sectionDetails.videos[0]?.contentType}` : '',
      }}
      contents={iconsWithTextDesktop}
      layoutType={sectionDetails.layoutType}
      className={classnames(sectionDetails.classNames)}
    />
  )
}

export default ImageWithTextProductDetailsSection
